import { supabase } from '../lib/supabase';

/**
 * Storage Service - Handles file uploads to Supabase Storage
 * Provides functions for uploading, retrieving, and deleting files
 */

export const storageService = {
  /**
   * Upload event image to Supabase Storage
   * @param {File} file - File object to upload
   * @param {string} userId - User ID for organizing files
   * @returns {Promise<string>} - Public URL of uploaded file
   */
  async uploadEventImage(file, userId) {
    try {
      // Extract file extension safely
      const fileExt = file?.name?.split('.')?.pop()?.toLowerCase() || 'jpg';
      
      // Generate ASCII-safe filename (no non-ASCII characters)
      const timestamp = Date.now();
      const randomString = Math.random()?.toString(36)?.substring(2, 9);
      const safeFileName = `${userId}/${timestamp}-${randomString}.${fileExt}`;

      // Upload file to event-images bucket
      const { data, error } = await supabase?.storage?.from('event-images')?.upload(safeFileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL for the uploaded file
      const { data: urlData } = supabase?.storage?.from('event-images')?.getPublicUrl(data?.path);

      return urlData?.publicUrl;
    } catch (error) {
      console.error('Error uploading event image:', error);
      throw error;
    }
  },

  /**
   * Upload multiple event images
   * @param {File[]} files - Array of file objects to upload
   * @param {string} userId - User ID for organizing files
   * @returns {Promise<string[]>} - Array of public URLs
   */
  async uploadMultipleEventImages(files, userId) {
    try {
      const uploadPromises = files?.map(file => 
        this.uploadEventImage(file, userId)
      );
      
      const urls = await Promise.all(uploadPromises);
      return urls;
    } catch (error) {
      console.error('Error uploading multiple images:', error);
      throw error;
    }
  },

  /**
   * Delete event image from storage
   * @param {string} filePath - Path of file to delete (extracted from public URL)
   * @returns {Promise<void>}
   */
  async deleteEventImage(filePath) {
    try {
      // Extract path from public URL if full URL is provided
      let path = filePath;
      if (filePath?.includes('event-images/')) {
        path = filePath?.split('event-images/')?.[1];
      }

      const { error } = await supabase?.storage?.from('event-images')?.remove([path]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting event image:', error);
      throw error;
    }
  },

  /**
   * Get public URL for existing file in storage
   * @param {string} filePath - Path of file in storage
   * @returns {string} - Public URL
   */
  getEventImagePublicUrl(filePath) {
    const { data } = supabase?.storage?.from('event-images')?.getPublicUrl(filePath);
    
    return data?.publicUrl;
  },

  /**
   * Upload spot image to Supabase Storage
   * @param {File} file - File object to upload
   * @param {string} userId - User ID for organizing files
   * @returns {Promise<string>} - Public URL of uploaded file
   */
  async uploadSpotImage(file, userId) {
    try {
      // Extract file extension safely
      const fileExt = file?.name?.split('.')?.pop()?.toLowerCase() || 'jpg';
      
      // Generate ASCII-safe filename (no non-ASCII characters)
      const timestamp = Date.now();
      const randomString = Math.random()?.toString(36)?.substring(2, 9);
      const safeFileName = `${userId}/${timestamp}-${randomString}.${fileExt}`;

      // Upload file to spot-images bucket
      const { data, error } = await supabase?.storage?.from('spot-images')?.upload(safeFileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL for the uploaded file
      const { data: urlData } = supabase?.storage?.from('spot-images')?.getPublicUrl(data?.path);

      return urlData?.publicUrl;
    } catch (error) {
      console.error('Error uploading spot image:', error);
      throw error;
    }
  },

  /**
   * Upload multiple spot images
   * @param {File[]} files - Array of file objects to upload
   * @param {string} userId - User ID for organizing files
   * @returns {Promise<string[]>} - Array of public URLs
   */
  async uploadMultipleSpotImages(files, userId) {
    try {
      const uploadPromises = files?.map(file => 
        this.uploadSpotImage(file, userId)
      );
      
      const urls = await Promise.all(uploadPromises);
      return urls;
    } catch (error) {
      console.error('Error uploading multiple spot images:', error);
      throw error;
    }
  },

  /**
   * Delete spot image from storage
   * @param {string} filePath - Path of file to delete (extracted from public URL)
   * @returns {Promise<void>}
   */
  async deleteSpotImage(filePath) {
    try {
      // Extract path from public URL if full URL is provided
      let path = filePath;
      if (filePath?.includes('spot-images/')) {
        path = filePath?.split('spot-images/')?.[1];
      }

      const { error } = await supabase?.storage?.from('spot-images')?.remove([path]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting spot image:', error);
      throw error;
    }
  }
};